import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import api from '@/api/axios';
import { Plus, Package, PencilSimple, FilePdf, Share } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const CURRENCY_SYMBOLS = { GTQ: 'Q', USD: '$', EUR: '€', MXN: '$' };

const Orders = () => {
  const { selectedBusiness } = useOutletContext();
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [formData, setFormData] = useState({
    customer_id: '',
    items: [{ name: '', quantity: 1, price: 0 }],
    payment_method: 'cash',
    delivery_address: '',
    phone1: '',
    phone2: '',
    courier_company: '',
    shipping_cost: 0,
    notes: ''
  });

  const currencySymbol = CURRENCY_SYMBOLS[selectedBusiness?.currency] || 'Q';

  useEffect(() => {
    if (selectedBusiness) {
      fetchOrders();
      fetchCustomers();
    }
  }, [selectedBusiness]);

  const fetchOrders = async () => {
    try {
      const response = await api.get('/orders', { params: { business_id: selectedBusiness.id } });
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers', { params: { business_id: selectedBusiness.id } });
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const downloadOrderPdf = async (orderId, docType = 'factura', share = false) => {
    try {
      const response = await api.get(`/orders/${orderId}/pdf`, {
        params: { doc_type: docType },
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const filename = `${docType}-${orderId.substring(0, 8)}.pdf`;
      const file = new File([blob], filename, { type: 'application/pdf' });
      if (share && navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: docType === 'factura' ? 'Factura / Pedido' : 'Orden de Despacho' });
        return;
      }
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      toast.error('No se pudo generar el PDF');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const total = formData.items.reduce((sum, item) => sum + (item.quantity * item.price), 0);
      const data = { ...formData, business_id: selectedBusiness.id, total };

      if (editingOrder) {
        await api.put(`/orders/${editingOrder.id}`, data);
        toast.success('Pedido actualizado');
      } else {
        await api.post('/orders', data);
        toast.success('Pedido creado');
      }
      setIsDialogOpen(false);
      setEditingOrder(null);
      resetForm();
      fetchOrders();
    } catch (error) {
      console.error('Error saving order:', error);
      toast.error('Error al guardar pedido');
    }
  };

  const handleEdit = (order) => {
    setEditingOrder(order);
    setFormData({
      customer_id: order.customer_id,
      items: order.items || [{ name: '', quantity: 1, price: 0 }],
      payment_method: order.payment_method || 'cash',
      delivery_address: order.delivery_address || '',
      phone1: order.phone1 || '',
      phone2: order.phone2 || '',
      courier_company: order.courier_company || '',
      shipping_cost: order.shipping_cost || 0,
      notes: order.notes || ''
    });
    setIsDialogOpen(true);
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      await api.put(`/orders/${orderId}`, { status });
      toast.success('Estado actualizado');
      fetchOrders();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const addItem = () => setFormData({ ...formData, items: [...formData.items, { name: '', quantity: 1, price: 0 }] });
  const removeItem = (index) => setFormData({ ...formData, items: formData.items.filter((_, i) => i !== index) });
  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    setFormData({ ...formData, items: newItems });
  };

  const resetForm = () => setFormData({
    customer_id: '', items: [{ name: '', quantity: 1, price: 0 }], payment_method: 'cash',
    delivery_address: '', phone1: '', phone2: '', courier_company: '', shipping_cost: 0, notes: ''
  });

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-orange-500/15 text-orange-400 border border-orange-500/30',
      confirmed: 'bg-blue-500/15 text-blue-400 border border-blue-500/30',
      delivered: 'bg-green-500/15 text-green-400 border border-green-500/30',
      cancelled: 'bg-red-500/15 text-red-400 border border-red-500/30'
    };
    return colors[status] || colors.pending;
  };

  const getCustomerName = (cid) => customers.find(c => c.id === cid)?.name || cid?.substring(0, 8);

  if (!selectedBusiness) return <div className="h-full flex items-center justify-center"><p className="text-sm text-[var(--text-muted)]">Selecciona un negocio</p></div>;

  const subtotal = formData.items.reduce((s, i) => s + (i.quantity * i.price), 0);
  const grandTotal = subtotal + (parseFloat(formData.shipping_cost) || 0);

  return (
    <div className="h-full p-6 overflow-y-auto bg-[var(--bg)]" data-testid="orders-page">
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] font-black text-[var(--text-faint)] mb-2">{selectedBusiness.name}</p>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-black text-[var(--text)]">Pedidos<span className="text-blue-500">.</span></h1>
        </div>
        <button
          onClick={() => { setEditingOrder(null); resetForm(); setIsDialogOpen(true); }}
          className="bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-[12px] px-5 py-3 flex items-center gap-2 transition-all duration-200 shadow-[0_4px_14px_0_rgba(59,130,246,0.4)] hover:shadow-[0_6px_20px_rgba(59,130,246,0.5)] hover:-translate-y-0.5"
          data-testid="create-order-btn"
        >
          <Plus size={18} weight="bold" /> Nuevo Pedido
        </button>
      </div>

      {/* Pipeline visual */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { key: 'pending', label: 'Nuevo', color: 'orange', icon: '🆕' },
          { key: 'confirmed', label: 'Confirmado', color: 'blue', icon: '✓' },
          { key: 'delivered', label: 'Entregado', color: 'green', icon: '📦' },
          { key: 'cancelled', label: 'Cancelado', color: 'red', icon: '✕' },
        ].map(stage => {
          const list = orders.filter(o => o.status === stage.key);
          const total = list.reduce((s, o) => s + (o.total || 0), 0);
          const colorMap = {
            orange: { border: 'border-orange-500/20', bg: 'bg-orange-500/15', text: 'text-orange-400', glow: 'shadow-[0_0_20px_rgba(249,115,22,0.1)]' },
            blue: { border: 'border-blue-500/20', bg: 'bg-blue-500/15', text: 'text-blue-400', glow: 'shadow-[0_0_20px_rgba(59,130,246,0.1)]' },
            green: { border: 'border-green-500/20', bg: 'bg-green-500/15', text: 'text-green-400', glow: 'shadow-[0_0_20px_rgba(16,185,129,0.1)]' },
            red: { border: 'border-red-500/20', bg: 'bg-red-500/15', text: 'text-red-400', glow: 'shadow-[0_0_20px_rgba(239,68,68,0.1)]' },
          };
          const c = colorMap[stage.color];
          return (
            <div key={stage.key} className={`bg-[var(--surface)] border ${c.border} ${c.glow} rounded-[16px] p-4 hover:-translate-y-1 transition-all duration-200`} data-testid={`pipeline-${stage.key}`}>
              <div className="flex items-center justify-between mb-2">
                <span className={`inline-flex items-center justify-center w-8 h-8 rounded-[10px] ${c.bg} text-sm`}>{stage.icon}</span>
                <span className={`text-[10px] uppercase tracking-[0.2em] font-black ${c.text}`}>{stage.label}</span>
              </div>
              <p className={`font-heading text-3xl font-black tracking-tighter ${c.text}`}>{list.length}</p>
              <p className="text-[11px] text-[var(--text-faint)] mt-1 font-medium">{currencySymbol}{total.toFixed(0)}</p>
            </div>
          );
        })}
      </div>

      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[16px] overflow-x-auto">
        <table className="w-full" data-testid="orders-table">
          <thead className="bg-white/[0.02] border-b border-[var(--border)]">
            <tr>
              <th className="px-4 py-3 text-left text-[10px] tracking-[0.2em] uppercase font-black text-[var(--text-faint)]">ID</th>
              <th className="px-4 py-3 text-left text-[10px] tracking-[0.2em] uppercase font-black text-[var(--text-faint)]">Cliente</th>
              <th className="px-4 py-3 text-left text-[10px] tracking-[0.2em] uppercase font-black text-[var(--text-faint)]">Total</th>
              <th className="px-4 py-3 text-left text-[10px] tracking-[0.2em] uppercase font-black text-[var(--text-faint)]">Envío</th>
              <th className="px-4 py-3 text-left text-[10px] tracking-[0.2em] uppercase font-black text-[var(--text-faint)]">Pago</th>
              <th className="px-4 py-3 text-left text-[10px] tracking-[0.2em] uppercase font-black text-[var(--text-faint)]">Estado</th>
              <th className="px-4 py-3 text-left text-[10px] tracking-[0.2em] uppercase font-black text-[var(--text-faint)]">Teléfono</th>
              <th className="px-4 py-3 text-left text-[10px] tracking-[0.2em] uppercase font-black text-[var(--text-faint)]">Courier</th>
              <th className="px-4 py-3 text-left text-[10px] tracking-[0.2em] uppercase font-black text-[var(--text-faint)]">Fecha</th>
              <th className="px-4 py-3 text-left text-[10px] tracking-[0.2em] uppercase font-black text-[var(--text-faint)]">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 ? (
              <tr><td colSpan="10" className="px-4 py-12 text-center text-sm text-[var(--text-faint)]"><Package size={32} className="mx-auto mb-2 text-slate-700" /> No hay pedidos registrados</td></tr>
            ) : orders.map((order) => (
              <tr key={order.id} className="border-b border-[var(--border)] hover:bg-white/[0.03] transition-colors" data-testid={`order-row-${order.id}`}>
                <td className="px-4 py-3 text-sm text-[var(--text)] font-mono font-bold">#{order.id.substring(0, 6)}</td>
                <td className="px-4 py-3 text-sm text-[var(--text)] font-medium">{getCustomerName(order.customer_id)}</td>
                <td className="px-4 py-3 text-sm text-green-400 font-black">{currencySymbol}{order.total?.toFixed(2)}</td>
                <td className="px-4 py-3 text-sm text-[var(--text-muted)]">{order.shipping_cost > 0 ? `${currencySymbol}${order.shipping_cost.toFixed(2)}` : '-'}</td>
                <td className="px-4 py-3"><span className="text-xs px-2 py-1 bg-white/5 border border-[var(--border)] rounded-full text-[var(--text)] font-medium">{order.payment_method}</span></td>
                <td className="px-4 py-3"><span className={`text-xs px-2.5 py-1 rounded-full font-bold uppercase tracking-wide ${getStatusColor(order.status)}`}>{order.status}</span></td>
                <td className="px-4 py-3 text-sm text-[var(--text-muted)]">{order.phone1 || '-'}</td>
                <td className="px-4 py-3 text-sm text-[var(--text-muted)]">{order.courier_company || '-'}</td>
                <td className="px-4 py-3 text-sm text-[var(--text-faint)]">{new Date(order.created_at).toLocaleDateString('es-ES')}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1 flex-wrap">
                    <button onClick={() => handleEdit(order)} className="p-2 hover:bg-white/10 rounded-[8px] text-[var(--text-muted)] hover:text-[var(--text)] transition-colors" data-testid={`edit-order-${order.id}`} title="Editar"><PencilSimple size={14} weight="bold" /></button>
                    <button onClick={() => downloadOrderPdf(order.id, 'factura')} className="p-2 hover:bg-white/10 rounded-[8px] text-blue-400 hover:text-blue-300 transition-colors" data-testid={`pdf-factura-${order.id}`} title="Descargar Factura PDF"><FilePdf size={14} weight="bold" /></button>
                    <button onClick={() => downloadOrderPdf(order.id, 'despacho')} className="p-2 hover:bg-white/10 rounded-[8px] text-emerald-400 hover:text-emerald-300 transition-colors" data-testid={`pdf-despacho-${order.id}`} title="Descargar Orden de Despacho"><Package size={14} weight="bold" /></button>
                    <button onClick={() => downloadOrderPdf(order.id, 'factura', true)} className="p-2 hover:bg-white/10 rounded-[8px] text-violet-400 hover:text-violet-300 transition-colors" data-testid={`share-pdf-${order.id}`} title="Compartir PDF"><Share size={14} weight="bold" /></button>
                    <select value={order.status} onChange={(e) => updateOrderStatus(order.id, e.target.value)} className="text-xs px-2 py-1 bg-white/5 border border-[var(--border)] rounded-[8px] text-[var(--text)] focus:border-blue-500/50 focus:outline-none" data-testid={`order-status-${order.id}`}>
                      <option value="pending">Pendiente</option>
                      <option value="confirmed">Confirmado</option>
                      <option value="delivered">Entregado</option>
                      <option value="cancelled">Cancelado</option>
                    </select>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) { setEditingOrder(null); resetForm(); } }}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingOrder ? 'Editar Pedido' : 'Nuevo Pedido'}</DialogTitle>
            <DialogDescription>{editingOrder ? 'Modifica los datos del pedido' : 'Agrega los productos y datos del pedido'}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="order-form">
            {!editingOrder && (
              <div>
                <Label>Cliente</Label>
                <select value={formData.customer_id} onChange={(e) => setFormData({ ...formData, customer_id: e.target.value })} required className="w-full text-sm p-2 bg-[var(--bg)] text-[var(--text)] border border-[var(--border)] rounded-[12px] focus:ring-2 focus:ring-[#1D4ED8] focus:outline-none" data-testid="order-customer-select">
                  <option value="">Seleccionar cliente</option>
                  {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}
            <div>
              <Label>Items</Label>
              {formData.items.map((item, idx) => (
                <div key={idx} className="flex gap-2 mb-2">
                  <Input placeholder="Producto" value={item.name} onChange={(e) => updateItem(idx, 'name', e.target.value)} required className="flex-1" />
                  <Input type="number" placeholder="Cant." value={item.quantity} onChange={(e) => updateItem(idx, 'quantity', parseInt(e.target.value) || 0)} required className="w-20" />
                  <Input type="number" step="0.01" placeholder="Precio" value={item.price} onChange={(e) => updateItem(idx, 'price', parseFloat(e.target.value) || 0)} required className="w-24" />
                  {formData.items.length > 1 && <Button type="button" variant="outline" onClick={() => removeItem(idx)} className="px-3 shrink-0">X</Button>}
                </div>
              ))}
              <Button type="button" variant="outline" onClick={addItem} className="w-full mt-1" size="sm">+ Agregar Item</Button>
            </div>
            <div>
              <Label>Metodo de Pago</Label>
              <select value={formData.payment_method} onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })} className="w-full text-sm p-2 bg-[var(--bg)] text-[var(--text)] border border-[var(--border)] rounded-[12px] focus:ring-2 focus:ring-[#1D4ED8] focus:outline-none">
                <option value="cash">Efectivo</option><option value="deposit">Deposito</option><option value="card">Tarjeta</option>
              </select>
            </div>
            <div>
              <Label>Direccion de Entrega</Label>
              <Textarea value={formData.delivery_address} onChange={(e) => setFormData({ ...formData, delivery_address: e.target.value })} rows={2} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Telefono 1</Label><Input value={formData.phone1} onChange={(e) => setFormData({ ...formData, phone1: e.target.value })} placeholder="Numero principal" /></div>
              <div><Label>Telefono 2</Label><Input value={formData.phone2} onChange={(e) => setFormData({ ...formData, phone2: e.target.value })} placeholder="Numero secundario" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Empresa Courier</Label><Input value={formData.courier_company} onChange={(e) => setFormData({ ...formData, courier_company: e.target.value })} placeholder="Nombre del courier" /></div>
              <div><Label>Valor de Envio ({currencySymbol})</Label><Input type="number" step="0.01" value={formData.shipping_cost} onChange={(e) => setFormData({ ...formData, shipping_cost: parseFloat(e.target.value) || 0 })} placeholder="0.00" data-testid="order-shipping-cost" /></div>
            </div>
            <div><Label>Notas</Label><Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={2} /></div>
            <div className="pt-3 border-t border-[var(--border)]">
              <p className="text-sm text-[var(--text-muted)]">Subtotal: {currencySymbol}{subtotal.toFixed(2)}</p>
              {parseFloat(formData.shipping_cost) > 0 && <p className="text-sm text-[var(--text-muted)]">Envio: {currencySymbol}{parseFloat(formData.shipping_cost).toFixed(2)}</p>}
              <p className="text-lg font-bold text-[var(--text)]">Total: {currencySymbol}{grandTotal.toFixed(2)}</p>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => { setIsDialogOpen(false); setEditingOrder(null); resetForm(); }}>Cancelar</Button>
              <Button type="submit" data-testid="save-order-btn">{editingOrder ? 'Guardar Cambios' : 'Crear Pedido'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Orders;
